import PropTypes from 'prop-types';
import { BsCheckCircleFill, BsCircle, BsX } from 'react-icons/bs';

import IconButton from '#globalComponents/IconButton/IconButton';

import { getActivityType } from '../../../constants/activityTypes';
import { isOverdue } from '../../../utils/activityStatus';

import ActivityCompletionControls from './ActivityCompletionControls';
import ActivityDrawerContent from './ActivityDrawerContent';

const ActivityDrawer = ({
	activity,
	members,
	onClose,
	onComplete,
	onCancelAutomated,
	onDelete,
	onReassign,
	onUpdate,
	navigate,
}) => {
	const type = getActivityType(activity.activityType);
	const Icon = type.icon;
	const dueDate = activity.dueAt ? new Date(activity.dueAt) : null;
	const overdue = isOverdue(activity);
	const isAutomated = Boolean(activity.isAutomated);
	const isCampaign = Boolean(activity.channelPayload?.campaignId);
	const canComplete = !isAutomated && (activity.status === 'planned' || activity.status === 'in_progress');
	const isMeetingOrCalendar = activity.activityType === 'meeting' || Boolean(activity.calendarProvider);
	const showCompletionControls = !isMeetingOrCalendar && !isAutomated;
	const isReadOnly = isCampaign;

	return (
		<aside className="w-[360px] flex-shrink-0 border-l border-bgBase-500 bg-white overflow-y-auto">
			<div className="flex items-center justify-between px-5 py-3.5 border-b border-bgBase-400">
				<div
					className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[12px] font-semibold"
					style={{ background: type.color + '1a', color: type.color }}
				>
					<Icon className="w-3.5 h-3.5" /> {type.label}
				</div>
				<IconButton icon={BsX} label="Schließen" onClick={onClose} className="h-7 w-7" />
			</div>

			<div className="flex items-start gap-3 px-5 py-4">
				{isAutomated && activity.status !== 'completed' ? (
					<span className="flex-shrink-0 mt-1 text-[9px] font-semibold bg-primary-200 text-primary-700 px-1.5 py-0.5 rounded-full">
						Auto
					</span>
				) : showCompletionControls ? (
					<button
						type="button"
						onClick={(e) => (canComplete || activity.status === 'completed') && onComplete(e, activity)}
						className="flex-shrink-0 mt-0.5"
					>
						{activity.status === 'completed' ? (
							<BsCheckCircleFill className="w-5 h-5 text-success-500" />
						) : (
							<BsCircle className="w-5 h-5 text-bgBase-800 hover:text-success-500 transition-colors" />
						)}
					</button>
				) : (
					<Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: type.color }} />
				)}
				<h2
					className={`text-md font-bold ${activity.status === 'completed' ? 'line-through text-bgBase-800' : 'text-text'}`}
				>
					{activity.title}
				</h2>
			</div>

			{showCompletionControls && (
				<ActivityCompletionControls
					activity={activity}
					dueDate={dueDate}
					isOverdue={overdue}
					onComplete={onComplete}
					onUpdate={onUpdate}
				/>
			)}

			<ActivityDrawerContent
				activity={activity}
				members={members}
				dueDate={dueDate}
				isOverdue={overdue}
				isAutomated={isAutomated}
				isMeetingOrCalendar={isMeetingOrCalendar}
				isReadOnly={isReadOnly}
				onReassign={onReassign}
				onUpdate={onUpdate}
				onDelete={onDelete}
				onCancelAutomated={onCancelAutomated}
				navigate={navigate}
			/>
		</aside>
	);
};

ActivityDrawer.propTypes = {
	activity: PropTypes.object.isRequired,
	members: PropTypes.array.isRequired,
	onClose: PropTypes.func.isRequired,
	onComplete: PropTypes.func.isRequired,
	onCancelAutomated: PropTypes.func,
	onDelete: PropTypes.func.isRequired,
	onReassign: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	navigate: PropTypes.func.isRequired,
};

export default ActivityDrawer;
